/**
 * crucible#2956 (Codex final) — build completion is reported ONLY with a durable
 * `build.terminal` proof, on EVERY consumer, not just completeBuild's return path.
 * The former phase-7-progress-only success path let the exact split this PR hardens
 * against (terminal progress/state advanced, the terminal event missing/hung/failed)
 * surface as `completed` to snapshot consumers (readFoundryGameStateSnapshot /
 * run-state / the GUI status) from `docs/build-progress.json` ALONE. So phase-7
 * progress is NOT completion proof on its own: a completed terminal that belongs to
 * the current run is required. A snapshot read path that has no terminal to pass
 * (e.g. the projection-only signal) correctly reports unverified/running, never
 * phase-7-optimistic completed.
 */
export function hasVerifiedBuildCompletionEvidence(input) {
    return hasVerifiedTerminalCompletion(input);
}
/**
 * Checks whether a terminal completion can close the current build snapshot.
 *
 * Args:
 *   input: Completion evidence collected from canonical events and progress files.
 *
 * Returns:
 *   True when the terminal belongs to the active build or is not older than known fresh work.
 *
 * Raises:
 *   None.
 */
function hasVerifiedTerminalCompletion(input) {
    const terminal = input.terminal ?? null;
    if (!terminal || terminal.terminalStatus !== 'completed')
        return false;
    if (input.gameId && terminal.gameId && terminal.gameId !== input.gameId)
        return false;
    const expectedBuildRunId = input.currentBuildRunId ?? input.progress?.buildSessionId;
    // crucible#2956 (Codex final) — when an expected run id EXISTS, the terminal must
    // PROVE it belongs to that run: require terminal.buildRunId to be present AND
    // equal. A missing (identity-less) or different terminal.buildRunId → NOT
    // verified. The previous check only rejected when BOTH ids were present, so an
    // identity-less completed terminal verified a specific run it couldn't prove it
    // was for. (No expected run id at all — a caller with no run context — keeps the
    // existing freshness/active-evidence behaviour below.)
    if (expectedBuildRunId !== undefined && terminal.buildRunId !== expectedBuildRunId) {
        return false;
    }
    const terminalAt = parseIsoMs(terminal.occurredAt);
    const freshnessCutoffAt = parseIsoMs(input.freshnessCutoffAt);
    if (terminal.buildRunId &&
        !expectedBuildRunId &&
        freshnessCutoffAt !== null &&
        (terminalAt === null || terminalAt < freshnessCutoffAt)) {
        // A newer interview marker means this terminal is from a previous run until proven otherwise.
        return false;
    }
    return !hasNewerDifferentActiveEvidence(input.activeEvidence, terminal);
}
function hasNewerDifferentActiveEvidence(activeEvidence, terminal) {
    const entries = activeEvidence ?? [];
    // crucible#2956 (Codex final) — an identity-less terminal can't prove it isn't
    // superseded by any active run: if ANY active evidence names a run, treat the
    // terminal as unproven (superseded). (This branch only matters when there is no
    // expected run id; a mismatch against an expected id already failed closed above.)
    if (!terminal.buildRunId) {
        return entries.some((entry) => entry.buildRunId !== undefined);
    }
    const terminalAt = parseIsoMs(terminal.occurredAt);
    return entries.some((entry) => {
        if (!entry.buildRunId || entry.buildRunId === terminal.buildRunId)
            return false;
        const observedAt = parseIsoMs(entry.observedAt);
        if (terminalAt === null || observedAt === null)
            return true;
        return observedAt > terminalAt;
    });
}
function parseIsoMs(value) {
    if (!value)
        return null;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
}
//# sourceMappingURL=build-completion-evidence.js.map